/* AURIQ Adaptive Tracker
 * Loaded on every storefront page via the auriq-tracker app embed block.
 * Graceful: any backend failure falls back to localStorage buffering; the
 * storefront never blocks on tracker state.
 */
(function () {
  "use strict";

  var cfg = window.AuriqConfig;
  if (!cfg || !cfg.proxyUrl) return;

  // ---- storage keys ----------------------------------------------------
  var KEY_VID = "auriq_vid";
  var KEY_HMAC = "auriq_vhmac";
  var KEY_SID = "auriq_sid";
  var KEY_PENDING = "auriq_pending";
  var ISSUE_BACKOFF_KEY = "auriq_issue_backoff_until";

  // ---- tiny safe helpers ----------------------------------------------
  function safeGet(store, k) { try { return store.getItem(k); } catch (e) { return null; } }
  function safeSet(store, k, v) { try { store.setItem(k, v); } catch (e) {} }
  function safeRemove(store, k) { try { store.removeItem(k); } catch (e) {} }
  function safeJSON(s) { try { return JSON.parse(s); } catch (e) { return null; } }

  // RFC 4122 UUIDv4 (sessionStorage session id is fine as v4 — we only need
  // randomness, not time-ordering, here)
  function uuidv4() {
    if (window.crypto && crypto.getRandomValues) {
      var b = new Uint8Array(16);
      crypto.getRandomValues(b);
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      var h = [];
      for (var i = 0; i < b.length; i++) h.push((b[i] + 0x100).toString(16).slice(1));
      return h[0]+h[1]+h[2]+h[3]+"-"+h[4]+h[5]+"-"+h[6]+h[7]+"-"+h[8]+h[9]+"-"+h[10]+h[11]+h[12]+h[13]+h[14]+h[15];
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c === "x" ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function setFirstPartyCookie(name, value) {
    try {
      var host = window.location.hostname;
      // Best-effort cookie domain: strip leading "www." and any subdomain
      // beyond two labels so .auriq.jewelry takes the apex. On myshopify.com
      // dev domains this won't stick — we rely on localStorage instead.
      var parts = host.split(".");
      var domain = parts.length >= 2 ? "." + parts.slice(-2).join(".") : host;
      var twoYears = 60 * 60 * 24 * 730;
      document.cookie = name + "=" + encodeURIComponent(value)
        + "; Max-Age=" + twoYears
        + "; Path=/"
        + "; Domain=" + domain
        + "; SameSite=Lax; Secure";
    } catch (e) {}
  }

  // ---- fingerprint (best-effort) --------------------------------------
  function fingerprint() {
    try {
      var c = document.createElement("canvas");
      var ctx = c.getContext("2d");
      if (!ctx) return navigator.userAgent + "|" + Math.random();
      ctx.textBaseline = "top";
      ctx.font = "14px Arial";
      ctx.fillStyle = "#f60";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.fillText("AURIQ", 2, 15);
      var data = c.toDataURL();
      var hash = 0;
      for (var i = 0; i < data.length; i++) hash = ((hash << 5) - hash + data.charCodeAt(i)) | 0;
      return navigator.userAgent + "|" + (hash >>> 0).toString(16);
    } catch (e) {
      return (navigator.userAgent || "") + "|" + Math.random().toString(16).slice(2);
    }
  }

  // ---- UTM extraction --------------------------------------------------
  function readUtm() {
    var u = {};
    try {
      var sp = new URLSearchParams(window.location.search);
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "gbraid", "wbraid"].forEach(function (k) {
        var v = sp.get(k);
        if (v) u[k] = v;
      });
    } catch (e) {}
    return u;
  }

  // ---- visitor id management ------------------------------------------
  function loadVisitor() {
    var id = safeGet(localStorage, KEY_VID);
    var hmac = safeGet(localStorage, KEY_HMAC);
    return id && hmac ? { id: id, hmac: hmac } : null;
  }

  function saveVisitor(id, hmac) {
    safeSet(localStorage, KEY_VID, id);
    safeSet(localStorage, KEY_HMAC, hmac);
    setFirstPartyCookie(KEY_VID, id);
  }

  // session id (sessionStorage, fresh per tab)
  function sessionId() {
    var s = safeGet(sessionStorage, KEY_SID);
    if (s) return s;
    s = uuidv4();
    safeSet(sessionStorage, KEY_SID, s);
    return s;
  }

  // ---- issue-visitor with backoff -------------------------------------
  var ISSUE_BACKOFF_STEPS_MS = [1000, 5000, 30000, 300000];
  var issueAttempts = 0;

  function nextBackoff() {
    var step = ISSUE_BACKOFF_STEPS_MS[Math.min(issueAttempts, ISSUE_BACKOFF_STEPS_MS.length - 1)];
    issueAttempts++;
    return Date.now() + step;
  }

  function tryIssueVisitor() {
    var until = parseInt(safeGet(localStorage, ISSUE_BACKOFF_KEY) || "0", 10);
    if (until > Date.now()) return Promise.resolve(null);

    var utm = readUtm();
    var body = {
      fingerprint: fingerprint(),
      user_agent: navigator.userAgent || "",
      landing_url: window.location.href,
      referrer: document.referrer || "",
      utm_source: utm.utm_source || "",
      utm_medium: utm.utm_medium || "",
      utm_campaign: utm.utm_campaign || "",
      utm_term: utm.utm_term || "",
      utm_content: utm.utm_content || "",
      gclid: utm.gclid || "",
      gbraid: utm.gbraid || "",
      wbraid: utm.wbraid || ""
    };

    return fetch(cfg.proxyUrl + "/issue-visitor", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error("issue " + r.status);
      return r.json();
    }).then(function (data) {
      if (!data || !data.visitor_id || !data.visitor_hmac) throw new Error("issue malformed");
      issueAttempts = 0;
      safeRemove(localStorage, ISSUE_BACKOFF_KEY);
      saveVisitor(data.visitor_id, data.visitor_hmac);
      return data;
    }).catch(function (err) {
      safeSet(localStorage, ISSUE_BACKOFF_KEY, String(nextBackoff()));
      return null;
    });
  }

  // ---- event queue + flush --------------------------------------------
  var queue = [];
  var pending = safeJSON(safeGet(localStorage, KEY_PENDING)) || [];
  if (Array.isArray(pending) && pending.length > 0) {
    queue = pending.slice(0, 200);
    safeRemove(localStorage, KEY_PENDING);
  }
  var flushTimer = null;
  var FLUSH_EVERY_MS = 5000;
  var FLUSH_SIZE = 20;
  var tempVisitorId = "tmp_" + uuidv4();

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(function () {
      flushTimer = null;
      flush(false);
    }, FLUSH_EVERY_MS);
  }

  function pushEvent(type, props) {
    if (!type) return;
    queue.push({
      session_id: sessionId(),
      ts_ms: Date.now(),
      type: type,
      page_url: window.location.href,
      props: props || {}
    });
    if (queue.length >= FLUSH_SIZE) flush(false);
    else scheduleFlush();
  }

  function persistOnFailure(events) {
    try {
      var combined = (safeJSON(safeGet(localStorage, KEY_PENDING)) || []).concat(events);
      safeSet(localStorage, KEY_PENDING, JSON.stringify(combined.slice(-200)));
    } catch (e) {}
  }

  function flush(useBeacon) {
    if (queue.length === 0) return;
    var visitor = loadVisitor();
    var rebindFrom = null;
    if (!visitor) {
      // No real visitor yet — kick off issue, drain into pending storage,
      // and bail. The next flush after issue succeeds will catch up.
      tryIssueVisitor();
      persistOnFailure(queue.splice(0, queue.length));
      return;
    }
    if (tempVisitorId) {
      rebindFrom = tempVisitorId;
      tempVisitorId = null;
    }
    var batch = queue.splice(0, FLUSH_SIZE);
    var payload = {
      visitor_id: visitor.id,
      visitor_hmac: visitor.hmac,
      events: batch
    };
    if (rebindFrom) payload.rebind_from_visitor_id = rebindFrom;

    var url = cfg.proxyUrl + "/track";
    if (useBeacon && navigator.sendBeacon) {
      try {
        var blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        var ok = navigator.sendBeacon(url, blob);
        if (!ok) persistOnFailure(batch);
        return;
      } catch (e) {
        persistOnFailure(batch);
        return;
      }
    }

    fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(function () {
      persistOnFailure(batch);
    });
  }

  // ---- identify -------------------------------------------------------
  function identify(kind, value) {
    if (!kind || !value) return;
    var visitor = loadVisitor();
    if (!visitor) return;
    fetch(cfg.proxyUrl + "/identify", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: visitor.id,
        visitor_hmac: visitor.hmac,
        kind: kind,
        value: value,
        confidence: 100
      })
    }).catch(function () {});
  }

  // ---- auto-track ------------------------------------------------------
  var SESSION_PV_KEY = "auriq_session_pv";
  var SESSION_MILESTONE_KEY = "auriq_session_milestone_fired";
  var SESSION_MILESTONE_PV = 5;

  function trackPageView() {
    pushEvent("page_view", {
      referrer: document.referrer || undefined,
      title: document.title ? document.title.slice(0, 512) : undefined
    });
    // One-shot deep-session milestone — fires exactly once per session when
    // crossing SESSION_MILESTONE_PV pageviews. Useful for rules that key off
    // "engaged session" without waiting for nightly analyses.
    try {
      var pv = parseInt(safeGet(sessionStorage, SESSION_PV_KEY) || "0", 10) || 0;
      pv++;
      safeSet(sessionStorage, SESSION_PV_KEY, String(pv));
      if (pv >= SESSION_MILESTONE_PV && !safeGet(sessionStorage, SESSION_MILESTONE_KEY)) {
        safeSet(sessionStorage, SESSION_MILESTONE_KEY, "1");
        pushEvent("deep_session_milestone", { page_views: pv });
      }
    } catch (e) {}
  }

  function trackPdpDwellOnUnload() {
    var pageType = cfg.pageType || "";
    if (pageType !== "product") return;
    var openedAt = Date.now();
    window.addEventListener("beforeunload", function () {
      pushEvent("pdp_dwell", {
        dwell_ms: Date.now() - openedAt,
        product_id: cfg.productId || undefined
      });
      flush(true);
    });
  }

  function interceptAddToCart() {
    var origFetch = window.fetch;
    if (typeof origFetch !== "function") return;
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var method = (init && init.method) || (typeof input !== "string" && input.method) || "GET";
      var promise = origFetch.apply(this, arguments);
      if (/\/cart\/add(\.js)?(\?|$)/.test(url) && /post/i.test(method)) {
        promise.then(function () {
          try { pushEvent("add_to_cart", { product_id: cfg.productId || undefined }); } catch (e) {}
        }, function () {});
      }
      return promise;
    };
  }

  // ---- expose API + boot ----------------------------------------------
  window.Auriq = {
    track: function (type, props) { try { pushEvent(type, props); } catch (e) {} },
    identify: function (kind, value) { try { identify(kind, value); } catch (e) {} },
    flush: function () { try { flush(false); } catch (e) {} },
    concierge: null
  };

  // Bootstrap: ensure visitor, send page_view, wire auto-trackers.
  var existing = loadVisitor();
  if (!existing) {
    tryIssueVisitor().then(function () {
      trackPageView();
      flush(false);
      // Probe fetch needs a real visitor_id/hmac to evaluate path-aware
      // rules; only kick it off once we have one.
      loadProbes();
    });
  } else {
    trackPageView();
    loadProbes();
  }

  if (cfg.hasCustomer && cfg.customerEmail) {
    identify("EMAIL", cfg.customerEmail);
  }
  if (cfg.hasCustomer && cfg.customerId) {
    var cid = String(cfg.customerId).replace(/[^0-9]/g, "");
    if (cid) identify("SHOPIFY_CUSTOMER", cid);
  }

  trackPdpDwellOnUnload();
  interceptAddToCart();

  // Last-chance flush on pagehide; beacon survives the unload.
  window.addEventListener("pagehide", function () { flush(true); });

  // ---- probes (in-page micro-survey) ---------------------------------
  var PROBE_SEEN_PREFIX = "auriq_probe_seen_";

  function probeSeenWithinCooldown(probeId, cooldownMinutes) {
    if (!probeId || !cooldownMinutes || cooldownMinutes <= 0) return false;
    var raw = safeGet(localStorage, PROBE_SEEN_PREFIX + probeId);
    if (!raw) return false;
    var seenAt = parseInt(raw, 10);
    if (!isFinite(seenAt)) return false;
    return Date.now() - seenAt < cooldownMinutes * 60000;
  }

  function markProbeSeen(probeId) {
    if (!probeId) return;
    safeSet(localStorage, PROBE_SEEN_PREFIX + probeId, String(Date.now()));
  }

  // Styles mirror auriq-concierge.js so both widgets read as one product
  // (Georgia serif, dark primary button, #fff background, !important locks
  // against theme CSS). Bottom-left placement keeps us off the concierge FAB.
  var PROBE_BOX_CSS = "position:fixed!important;left:16px!important;bottom:16px!important;width:320px!important;max-width:calc(100vw - 32px)!important;background:#fff!important;border:1px solid #ddd!important;border-radius:10px!important;box-shadow:0 8px 24px rgba(0,0,0,.12)!important;font-family:Georgia,serif!important;color:#111!important;padding:14px 16px!important;z-index:2147483646!important;";
  var PROBE_PROMPT_CSS = "font-size:14px!important;font-weight:600!important;line-height:1.4!important;color:#111!important;letter-spacing:0.04em!important;margin-bottom:12px!important;";
  var PROBE_CONTROLS_CSS = "display:flex!important;flex-wrap:wrap!important;gap:8px!important;";
  var PROBE_OPTION_BTN_CSS = "padding:8px 14px!important;border:1px solid #222!important;background:#fff!important;color:#111!important;border-radius:4px!important;cursor:pointer!important;font-family:Georgia,serif!important;font-size:13px!important;line-height:1!important;";
  var PROBE_PRIMARY_BTN_CSS = "padding:8px 14px!important;border:1px solid #222!important;background:#222!important;color:#fff!important;border-radius:4px!important;cursor:pointer!important;font-family:Georgia,serif!important;font-size:13px!important;font-weight:500!important;";
  var PROBE_INPUT_CSS = "flex:1!important;min-width:0!important;padding:8px 10px!important;border:1px solid #ccc!important;border-radius:4px!important;font-family:Georgia,serif!important;font-size:13px!important;background:#fff!important;color:#111!important;outline:none!important;";
  var PROBE_DISMISS_CSS = "display:inline-block!important;margin-top:10px!important;font-family:Georgia,serif!important;font-size:11px!important;color:#888!important;cursor:pointer!important;letter-spacing:0.04em!important;";

  function makeProbeOptionButton(label, onClick) {
    var b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.style.cssText = PROBE_OPTION_BTN_CSS;
    b.onclick = onClick;
    return b;
  }

  function renderProbe(probe) {
    var box = document.createElement("div");
    box.setAttribute("data-auriq-probe", probe.id);
    box.style.cssText = PROBE_BOX_CSS;

    var prompt = document.createElement("div");
    prompt.style.cssText = PROBE_PROMPT_CSS;
    prompt.textContent = probe.prompt;
    box.appendChild(prompt);

    var controls = document.createElement("div");
    controls.style.cssText = PROBE_CONTROLS_CSS;

    function close() { try { box.remove(); } catch (e) {} }
    function submit(answer) {
      var visitor = loadVisitor();
      if (!visitor) { close(); return; }
      fetch(cfg.proxyUrl + "/probe-response", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          visitor_id: visitor.id, visitor_hmac: visitor.hmac,
          probe_id: probe.id, answer: String(answer).slice(0, 512)
        })
      }).catch(function () {});
      close();
    }

    if (probe.kind === "YES_NO") {
      ["Yes", "No"].forEach(function (a) {
        controls.appendChild(makeProbeOptionButton(a, function () { submit(a); }));
      });
    } else if (probe.kind === "ENUM" && Array.isArray(probe.enum_values)) {
      probe.enum_values.forEach(function (a) {
        controls.appendChild(makeProbeOptionButton(a, function () { submit(a); }));
      });
    } else if (probe.kind === "RATING_1_5") {
      [1, 2, 3, 4, 5].forEach(function (n) {
        controls.appendChild(makeProbeOptionButton(String(n), function () { submit(n); }));
      });
    } else {
      var input = document.createElement("input");
      input.type = "text";
      input.style.cssText = PROBE_INPUT_CSS;
      input.addEventListener("keydown", function (ev) {
        if (ev.key === "Enter" && input.value.trim()) submit(input.value.trim());
      });
      var send = document.createElement("button");
      send.type = "button";
      send.textContent = "Send";
      send.style.cssText = PROBE_PRIMARY_BTN_CSS;
      send.onclick = function () { if (input.value.trim()) submit(input.value.trim()); };
      controls.appendChild(input);
      controls.appendChild(send);
    }

    var dismiss = document.createElement("a");
    dismiss.textContent = "Dismiss";
    dismiss.style.cssText = PROBE_DISMISS_CSS;
    dismiss.onclick = function () {
      try { pushEvent("probe_dismissed", { probe_id: probe.id }); } catch (e) {}
      close();
    };

    box.appendChild(controls);
    box.appendChild(dismiss);
    // probe_shown fires through the regular event batcher; ingest.ts converts
    // it into a probe_impressions row (server-side cooldown source of truth).
    try { pushEvent("probe_shown", { probe_id: probe.id }); } catch (e) {}
    markProbeSeen(probe.id);
    document.body.appendChild(box);
  }

  function loadProbes() {
    var visitor = loadVisitor();
    if (!visitor) return;
    var params =
      "context=" + encodeURIComponent(cfg.pageType || "") +
      "&visitor_id=" + encodeURIComponent(visitor.id) +
      "&visitor_hmac=" + encodeURIComponent(visitor.hmac) +
      "&session_id=" + encodeURIComponent(sessionId()) +
      "&page_url=" + encodeURIComponent(window.location.href);
    fetch(cfg.proxyUrl + "/probes?" + params, { credentials: "include" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !Array.isArray(data.probes) || data.probes.length === 0) return;
        var probe = data.probes[0];
        if (!probe || !probe.id) return;
        // Belt-and-suspenders: server already filtered by impression cooldown,
        // but if the impression event was lost or hasn't landed yet we still
        // honor the local seen-at marker.
        if (probeSeenWithinCooldown(probe.id, probe.cooldown_minutes)) return;
        setTimeout(function () { renderProbe(probe); }, 4000);
      })
      .catch(function () {});
  }

  // Bootstrap concierge if its asset loaded alongside us.
  setTimeout(function () {
    if (window.Auriq && window.Auriq.concierge && typeof window.Auriq.concierge.init === "function") {
      try { window.Auriq.concierge.init(); } catch (e) {}
    }
  }, 3000);
})();
